import { useEffect, useState } from "react";
import AuthoritiesPage from "@/pages/superadmin/Authorities";
import { Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import Shell from "@/layout/Shell";
import KpiGrid from "@/components/KpiGrid";
import Queue from "@/components/Queue";
import { CampusRadar, PlatformCategoryPie, TicketsPerInstitute, PlatformGrowth } from "@/components/Charts";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Building2, Users, ShieldCheck, Zap, Mail, Plus, RefreshCw, Send,
  Search, KeyRound, Ban, CheckCircle2, Palette,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { colorFor, initialsOf } from "@/lib/mock";

/* ============================================================
 * Overview
 * ============================================================ */
function useOverview() {
  return useFetch(() => api.saOverview(), []);
}

function NewInstituteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: "", code: "", email_mode: "domain", allowed_domains: "", require_approval: false,
    passwordless: false, plan: "pilot", pilot_ends_at: "", contact_name: "", contact_email: "",
    admin_name: "", admin_email: "", admin_password: "",
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.code.trim()) { toast.error("Name and code are required."); return; }
    setBusy(true);
    try {
      const res = await api.saCreateInstitute({
        name: f.name.trim(), code: f.code.trim().toUpperCase(),
        email_mode: f.email_mode === "any" ? "any" : "domain",
        allowed_domains: f.allowed_domains, require_approval: f.require_approval,
        passwordless: f.passwordless, plan: f.plan, pilot_ends_at: f.pilot_ends_at || undefined,
        contact_name: f.contact_name || undefined, contact_email: f.contact_email || undefined,
        admin: f.admin_email && f.admin_password
          ? { name: f.admin_name || f.name, email: f.admin_email, password: f.admin_password }
          : undefined,
      });
      toast.success(`${res.institute.name} onboarded. Setup code: ${res.admin_setup_code}`);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.message || "Could not create institute");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-institute-btn" className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5">
          <Plus className="h-4 w-4" /> New institute
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Onboard an institute</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="ni-name" value={f.name} onChange={set("name")} className="mt-1" required /></div>
            <div><Label>Code</Label><Input data-testid="ni-code" value={f.code} onChange={set("code")} className="mt-1 uppercase text-mono" required /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email policy</Label>
              <Select value={f.email_mode} onValueChange={(v) => set("email_mode")(v)}>
                <SelectTrigger className="mt-1" data-testid="ni-email-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domain">Domain (college email only)</SelectItem>
                  <SelectItem value="any">Open (any email, gated by approval)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Plan</Label>
              <Select value={f.plan} onValueChange={(v) => set("plan")(v)}>
                <SelectTrigger className="mt-1" data-testid="ni-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pilot">Pilot</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {f.email_mode === "domain" && (
            <div><Label>Allowed domains (comma separated)</Label>
              <Input data-testid="ni-domains" placeholder="bkit.edu.in, bkit.ac.in" value={f.allowed_domains} onChange={set("allowed_domains")} className="mt-1 text-mono" />
            </div>
          )}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.require_approval} onCheckedChange={(v) => set("require_approval")(v)} /> Require approval</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.passwordless} onCheckedChange={(v) => set("passwordless")(v)} /> Passwordless OTP</label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Pilot ends</Label><Input type="date" value={f.pilot_ends_at} onChange={set("pilot_ends_at")} className="mt-1" /></div>
            <div><Label>Contact email</Label><Input value={f.contact_email} onChange={set("contact_email")} className="mt-1" /></div>
          </div>
          <div className="pt-3 border-t space-y-3">
            <div className="text-xs text-muted-foreground">Optional — create the institute admin now (else share the setup code separately)</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin name</Label><Input value={f.admin_name} onChange={set("admin_name")} className="mt-1" /></div>
              <div><Label>Admin email</Label><Input value={f.admin_email} onChange={set("admin_email")} className="mt-1" /></div>
            </div>
            <div><Label>Admin password</Label><Input type="password" value={f.admin_password} onChange={set("admin_password")} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="ni-submit" disabled={busy} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Creating…" : "Create institute"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Overview() {
  const ov = useOverview();
  const ch = useFetch(() => api.saCharts(), []);
  const g = ov.data?.global || {};
  const institutes = ov.data?.institutes || [];

  const totalUsers = institutes.reduce((s, c) => s + (c.stats?.users || 0), 0);
  const series = institutes.slice(0, 8).map((i) => ({
    code: i.code || i.name?.slice(0, 5) || "?",
    open: i.stats?.open || 0,
    closed: i.stats?.resolvedClosed || 0,
  }));

  return (
    <Shell role="superadmin" title="Platform Control" subtitle="Multi-campus health at a glance." actions={<NewInstituteDialog onCreated={ov.reload} />}>
      <KpiGrid
        items={[
          { label: "Institutes",   value: institutes.length,             accent: "#0047FF", note: "on the platform" },
          { label: "Active users", value: totalUsers.toLocaleString(),  accent: "#22D3EE", note: "across campuses" },
          { label: "Open tickets", value: g.open || 0,                  accent: "#F59E0B", note: `${g.today || 0} today` },
          { label: "Total",        value: g.tickets || 0,               accent: "#10B981", note: "all-time" },
        ]}
      />

      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
        <CampusRadar series={series} />
        <div className="glass rounded-2xl p-5" data-testid="system-health">
          <div className="overline">Platform health</div>
          <h3 className="font-display text-2xl font-extrabold tracking-tighter mt-1">Live signals</h3>
          <ul className="mt-4 space-y-3 text-sm">
            {[
              { name: "Worker API",     status: "operational", desc: process.env.REACT_APP_API_URL?.replace(/^https?:\/\//,"") || "" },
              { name: "Routing engine", status: "operational", desc: "auto-assigns on create" },
              { name: "OTP mail",       status: "operational", desc: "resend / smtp relay" },
              { name: "Escalation cron",status: "operational", desc: "SLA & reminders" },
            ].map((s) => (
              <li key={s.name} className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-lg grid place-items-center bg-emerald-500/12 text-emerald-600">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.desc}</div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-600">{s.status}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid xl:grid-cols-2 gap-6">
        <PlatformCategoryPie byCategory={ch.data?.byCategory || []} />
        <TicketsPerInstitute byInstitute={ch.data?.byInstitute || []} />
      </div>
      <PlatformGrowth growth={ch.data?.growth || []} />

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Institutes</div>
            <h3 className="font-display text-2xl font-extrabold tracking-tighter">Campus overview</h3>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="campus-grid">
          {institutes.map((c, i) => {
            const sla = c.stats?.tickets ? Math.round(((c.stats.tickets - (c.stats.overdue || 0)) / c.stats.tickets) * 100) : 100;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl p-5 relative overflow-hidden hover:-translate-y-0.5 transition-transform"
                data-testid={`campus-card-${c.code || c.id}`}
              >
                <div className="absolute -top-8 -right-8 h-24 w-24 rounded-full opacity-30 blur-2xl" style={{ background: colorFor(c.id) }} />
                <div className="flex items-center gap-2">
                  <span className="text-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full"
                        style={{ background: `${colorFor(c.id)}18`, color: colorFor(c.id) }}>
                    {c.code}
                  </span>
                  <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-emerald-600">
                    {c.is_active === false ? "inactive" : "active"}
                  </span>
                </div>
                <div className="mt-2 font-display text-xl font-extrabold tracking-tighter">{c.name}</div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Kv label="Users"   v={c.stats?.users?.toLocaleString?.() ?? c.stats?.users ?? 0} />
                  <Kv label="Tickets" v={c.stats?.tickets ?? 0} />
                  <Kv label="Open"    v={c.stats?.open ?? 0} />
                </div>
                <Progress value={sla} className="h-1 mt-3" />
                <div className="mt-1 text-[10px] text-mono uppercase tracking-widest text-muted-foreground">
                  {sla}% SLA · {c.stats?.techs ?? 0} techs
                </div>
              </motion.div>
            );
          })}
          {institutes.length === 0 && (
            <div className="text-sm text-muted-foreground col-span-full glass rounded-2xl p-6">No institutes yet.</div>
          )}
        </div>
      </div>
    </Shell>
  );
}

const Kv = ({ label, v }) => (
  <div>
    <div className="overline">{label}</div>
    <div className="metric text-lg mt-0.5">{v}</div>
  </div>
);

/* ============================================================
 * Institutes — tabbed drawer with full edit
 * ============================================================ */
/* WhatsApp channel editor — superadmin only. Each institute gets its own
 * Gupshup source number + API key, assigned here (never self-service by the
 * institute admin, since Arventiq Labs owns the Gupshup account/wallet). */
function InstituteWhatsAppTab({ instituteId }) {
  const [cfg, setCfg] = useState({ phone_number_id: "", phone_display: "", is_active: true, requester_flow_enabled: true });
  const [hasToken, setHasToken] = useState(false);
  const [keyFingerprint, setKeyFingerprint] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.saWhatsappConfig(instituteId)
      .then((r) => { if (!live) return; if (r.config) setCfg((s) => ({ ...s, ...r.config })); setHasToken(!!r.has_token); setKeyFingerprint(r.key_fingerprint || null); })
      .catch(() => {})
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [instituteId]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (apiKey) payload.access_token = apiKey;
      await api.saSaveWhatsappConfig(instituteId, payload);
      toast.success("WhatsApp settings saved");
      setApiKey("");
      const r = await api.saWhatsappConfig(instituteId);
      setHasToken(!!r.has_token); setKeyFingerprint(r.key_fingerprint || null);
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Channel active</div>
          <div className="text-xs text-muted-foreground">Turn off to stop this institute's WhatsApp bot without deleting its settings.</div>
        </div>
        <Switch checked={cfg.is_active} onCheckedChange={(v) => setCfg((s) => ({ ...s, is_active: v }))} />
      </div>
      <div className="rounded-xl border p-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">Allow ticket raising via WhatsApp</div>
          <div className="text-xs text-muted-foreground">
            Staff (admin, authority, technician) always keep full access regardless of this setting.
            Turn off to restrict this channel to staff only — students/requesters won't be able to
            open new tickets over WhatsApp, which keeps message volume (and cost) down.
          </div>
        </div>
        <Switch checked={cfg.requester_flow_enabled !== false} onCheckedChange={(v) => setCfg((s) => ({ ...s, requester_flow_enabled: v }))} />
      </div>
      <div>
        <Label>Phone Number ID <span className="text-muted-foreground font-normal">(Gupshup → Account tab → "Phone Number ID")</span></Label>
        <Input value={cfg.phone_number_id} onChange={(e) => setCfg((s) => ({ ...s, phone_number_id: e.target.value }))}
          placeholder="e.g. 1250827084787520 — used to match incoming messages" className="mt-1" />
      </div>
      <div>
        <Label>WhatsApp number <span className="text-muted-foreground font-normal">(Gupshup → Account tab → "Phone Number", used to send)</span></Label>
        <Input value={cfg.phone_display || ""} onChange={(e) => setCfg((s) => ({ ...s, phone_display: e.target.value }))}
          placeholder="e.g. 15553546356 or +91 78348 11114" className="mt-1" />
      </div>
      <div>
        <Label>Gupshup API key {hasToken && <span className="text-emerald-600">· a key is saved</span>}</Label>
        <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
          placeholder={hasToken ? "•••••••• (leave blank to keep current)" : "Paste this institute's Gupshup API key"} className="mt-1" />
        {hasToken && keyFingerprint && !keyFingerprint.broken && (
          <div className="text-xs text-muted-foreground mt-1">
            Stored key: {keyFingerprint.length} characters, ends in "{keyFingerprint.last4}" — compare against Gupshup's Settings tab to confirm it matches exactly.
          </div>
        )}
        {hasToken && keyFingerprint && keyFingerprint.broken && (
          <div className="text-xs text-red-600 mt-1">A key is stored but can't be decrypted — re-paste it and save again.</div>
        )}
      </div>
      <Button disabled={saving || !cfg.phone_number_id} onClick={save} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
        {saving ? "Saving…" : "Save WhatsApp settings"}
      </Button>
      <div className="text-xs text-muted-foreground pt-2 border-t">
        Callback URL in Gupshup's Webhooks tab: <span className="text-mono">https://&lt;worker&gt;/api/wa/webhook</span><br />
        Payload Format in Gupshup: <span className="text-mono">Meta format (v3)</span>.
      </div>
    </div>
  );
}

function InstituteDrawer({ inst, onClose, onSaved }) {
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => ({
    name: inst?.name || "", contact_name: inst?.contact_name || "", contact_email: inst?.contact_email || "",
    address: inst?.address || "", plan: inst?.plan || "pilot", pilot_ends_at: inst?.pilot_ends_at?.slice(0, 10) || "",
    is_active: inst?.is_active !== false,
    email_mode: inst?.email_mode || "domain", allowed_domains: inst?.allowed_domains || "",
    require_approval: !!inst?.require_approval, passwordless: !!inst?.passwordless,
    sso_enabled: !!inst?.sso_enabled,
    mail_mode: inst?.mail_mode || "shared", mail_from_email: inst?.mail_from_email || "", mail_from_name: inst?.mail_from_name || "",
    smtp_host: inst?.smtp_host || "", smtp_port: inst?.smtp_port || "", smtp_user: inst?.smtp_user || "",
    smtp_pass: "", smtp_relay_url: inst?.smtp_relay_url || "", smtp_relay_token: "", mail_provider: inst?.mail_provider || "resend",
    logo_url: inst?.logo_url || "", primary_color: inst?.primary_color || "#0047FF", accent_color: inst?.accent_color || "#22D3EE",
  }));
  const [testTo, setTestTo] = useState("");
  const users = useFetch(() => inst ? api.saInstituteUsers(inst.id).then(r => r?.users || []) : Promise.resolve([]), [inst?.id], { enabled: !!inst });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  if (!inst) return null;

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.saUpdateInstitute(inst.id, patch);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const delInstitute = async () => {
    const code = window.prompt(`This permanently deletes "${inst.name}" and ALL its users, tickets and data.\n\nType the institute code (${inst.code}) to confirm:`);
    if (!code) return;
    setBusy(true);
    try {
      await api.saDeleteInstitute(inst.id, code);
      toast.success(`${inst.code} deleted`);
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(e.message || "Delete failed"); }
    finally { setBusy(false); }
  };
  const regenCode = async () => {
    try {
      const r = await api.saRegenerateSetupCode(inst.id);
      toast.success(`New setup code: ${r.admin_setup_code}`);
    } catch (e) { toast.error(e.message); }
  };

  const sendTest = async () => {
    if (!testTo) return;
    try {
      const r = await api.saTestEmail(inst.id, testTo);
      toast.success(`Test sent via ${r.resolved?.via || "resolved config"}`);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Sheet open={!!inst} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <span className="text-mono text-xs px-2 py-0.5 rounded-full" style={{ background: `${colorFor(inst.id)}18`, color: colorFor(inst.id) }}>{inst.code}</span>
            {inst.name}
          </SheetTitle>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="policy">Access</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <details className="rounded-xl border p-3 bg-muted/30" open>
              <summary className="text-sm font-medium cursor-pointer select-none">
                Raw stored data — exactly what's in the database right now
              </summary>
              <p className="text-xs text-muted-foreground mt-1.5 mb-2">
                This is <code>inst</code> as returned by the server on this drawer's last open — not the form
                below, which only reflects what you've typed. If a field looks wrong here, it's wrong in
                Supabase; if it's right here but wrong in the form above, that's a separate display bug.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono max-h-64 overflow-y-auto">
                {Object.entries(inst).filter(([k]) => k !== "stats").sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => (
                  <div key={k} className="contents">
                    <div className="text-muted-foreground truncate">{k}</div>
                    <div className="truncate">
                      {v === null ? <span className="text-red-500">null</span>
                        : v === "" ? <span className="text-red-500">"" (empty)</span>
                        : typeof v === "boolean" ? <span className={v ? "text-emerald-600" : "text-muted-foreground"}>{String(v)}</span>
                        : String(v)}
                    </div>
                  </div>
                ))}
              </div>
            </details>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" /></div>
              <div>
                <Label>Plan</Label>
                <Select value={f.plan} onValueChange={set("plan")}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pilot">Pilot</SelectItem>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact name</Label><Input value={f.contact_name} onChange={set("contact_name")} className="mt-1" /></div>
              <div><Label>Contact email</Label><Input value={f.contact_email} onChange={set("contact_email")} className="mt-1" /></div>
            </div>
            <div><Label>Address</Label><Input value={f.address} onChange={set("address")} className="mt-1" /></div>
            <div><Label>Pilot ends</Label><Input type="date" value={f.pilot_ends_at} onChange={set("pilot_ends_at")} className="mt-1" /></div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <div className="text-sm font-medium">Tenant active</div>
                <div className="text-xs text-muted-foreground">Suspending instantly locks out all users of this institute.</div>
              </div>
              <Switch checked={f.is_active} onCheckedChange={(v) => { set("is_active")(v); save({ is_active: v }); }} data-testid="inst-active-toggle" />
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div className="text-sm">Admin setup code</div>
              <Button type="button" size="sm" variant="outline" onClick={regenCode} className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Regenerate</Button>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-red-200 bg-red-50/50 p-3">
              <div>
                <div className="text-sm font-medium text-red-700">Delete institute</div>
                <div className="text-xs text-red-600/80">Permanent. Removes all users, tickets &amp; data. Prefer Suspend.</div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={delInstitute} disabled={busy}
                className="gap-1.5 border-red-300 text-red-700 hover:bg-red-100">Delete</Button>
            </div>
            <Button disabled={busy} onClick={() => save({ name: f.name, contact_name: f.contact_name, contact_email: f.contact_email, address: f.address, plan: f.plan, pilot_ends_at: f.pilot_ends_at || null })}
              className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Saving…" : "Save overview"}
            </Button>
          </TabsContent>

          <TabsContent value="policy" className="space-y-4 mt-4">
            <div>
              <Label>Email policy</Label>
              <Select value={f.email_mode} onValueChange={set("email_mode")}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="domain">Domain-restricted</SelectItem>
                  <SelectItem value="any">Open (any email)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Allowed domains</Label><Input value={f.allowed_domains} onChange={set("allowed_domains")} className="mt-1 text-mono" placeholder="bkit.edu.in, bkit.ac.in" /></div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.require_approval} onCheckedChange={set("require_approval")} /> Require approval</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.passwordless} onCheckedChange={set("passwordless")} /> Passwordless OTP</label>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.sso_enabled} onCheckedChange={set("sso_enabled")} /> SSO with Events (auditorium & booking app)</label>
              <p className="text-xs text-muted-foreground mt-1 ml-11">
                Lets signed-in users of this institute jump between Solution and Events without signing in twice. No code changes needed — just toggle this, and the matching toggle on the Events side under Platform Admin → Institutes.
              </p>
            </div>
            <Button disabled={busy} onClick={() => save({ email_mode: f.email_mode, allowed_domains: f.allowed_domains, require_approval: f.require_approval, passwordless: f.passwordless, sso_enabled: f.sso_enabled })}
              className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Saving…" : "Save access & policy"}
            </Button>
          </TabsContent>

          <TabsContent value="email" className="space-y-4 mt-4">
            <div>
              <Label>Sending mode</Label>
              <Select value={f.mail_mode} onValueChange={set("mail_mode")}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="shared">Shared (Arventiq domain)</SelectItem>
                  <SelectItem value="smtp">SMTP (their mailbox)</SelectItem>
                  <SelectItem value="api">API key (Resend/Brevo)</SelectItem>
                  <SelectItem value="gas">Google Apps Script</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>From email</Label><Input value={f.mail_from_email} onChange={set("mail_from_email")} className="mt-1" /></div>
              <div><Label>From name</Label><Input value={f.mail_from_name} onChange={set("mail_from_name")} className="mt-1" /></div>
            </div>
            {f.mail_mode === "smtp" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>SMTP host</Label><Input value={f.smtp_host} onChange={set("smtp_host")} className="mt-1" /></div>
                  <div><Label>SMTP port</Label><Input value={f.smtp_port} onChange={set("smtp_port")} className="mt-1" /></div>
                </div>
                <div><Label>SMTP user</Label><Input value={f.smtp_user} onChange={set("smtp_user")} className="mt-1" /></div>
                <div><Label>SMTP app-password (leave blank to keep current)</Label><Input type="password" value={f.smtp_pass} onChange={set("smtp_pass")} className="mt-1" /></div>
                <div><Label>Relay URL</Label><Input value={f.smtp_relay_url} onChange={set("smtp_relay_url")} className="mt-1" /></div>
              </>
            )}
            {f.mail_mode === "gas" && (
              <>
                <div><Label>Google Apps Script web-app URL</Label>
                  <Input value={f.smtp_relay_url} onChange={set("smtp_relay_url")} placeholder="https://script.google.com/macros/s/…/exec" className="mt-1" /></div>
                <div><Label>Shared secret (leave blank to keep current)</Label>
                  <Input type="password" value={f.smtp_pass} onChange={set("smtp_pass")} placeholder="the secret you set in Code.gs" className="mt-1" /></div>
                <div className="text-xs text-muted-foreground">Deploy <span className="text-mono">apps-script/Code.gs</span> as a web app (Execute as: Me · Access: Anyone), paste its <b>/exec</b> URL above, and enter the same shared secret you set inside the script. From-email should match the Gmail account that owns the script.</div>
              </>
            )}
            {f.mail_mode === "api" && (
              <>
                <div><Label>Provider</Label>
                  <Select value={f.mail_provider || "resend"} onValueChange={set("mail_provider")}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resend">Resend</SelectItem>
                      <SelectItem value="brevo">Brevo</SelectItem>
                    </SelectContent>
                  </Select></div>
                <div><Label>API key (leave blank to keep current)</Label>
                  <Input type="password" value={f.smtp_pass} onChange={set("smtp_pass")} placeholder="re_… or xkeysib-…" className="mt-1" /></div>
                <div className="text-xs text-muted-foreground">The provider API key is stored encrypted. From-email must be a verified sender/domain on that provider.</div>
              </>
            )}
            {f.mail_mode === "shared" && (
              <div className="text-xs text-muted-foreground">Uses the Arventiq platform sender configured under <b>Platform email</b>. No per-institute keys needed. From-email/name above are optional overrides for display.</div>
            )}
            <Button disabled={busy} onClick={() => save({
              mail_mode: f.mail_mode, mail_from_email: f.mail_from_email, mail_from_name: f.mail_from_name,
              smtp_host: f.smtp_host, smtp_port: f.smtp_port, smtp_user: f.smtp_user,
              mail_provider: f.mail_provider || undefined,
              ...(f.smtp_pass ? { smtp_pass: f.smtp_pass } : {}),
              smtp_relay_url: f.smtp_relay_url, ...(f.smtp_relay_token ? { smtp_relay_token: f.smtp_relay_token } : {}),
            })} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Saving…" : "Save email config"}
            </Button>
            <div className="pt-3 border-t flex gap-2">
              <Input placeholder="Send test to…" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="flex-1" />
              <Button type="button" variant="outline" onClick={sendTest} className="gap-1.5"><Send className="h-3.5 w-3.5" /> Test</Button>
            </div>
          </TabsContent>

          <TabsContent value="whatsapp" className="mt-4">
            <InstituteWhatsAppTab instituteId={inst.id} />
          </TabsContent>

          <TabsContent value="branding" className="space-y-4 mt-4">
            <div><Label>Logo URL</Label><Input value={f.logo_url} onChange={set("logo_url")} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={f.primary_color} onChange={(e) => set("primary_color")(e.target.value)} className="h-9 w-12 rounded" />
                  <Input value={f.primary_color} onChange={set("primary_color")} className="text-mono" />
                </div>
              </div>
              <div>
                <Label>Accent color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={f.accent_color} onChange={(e) => set("accent_color")(e.target.value)} className="h-9 w-12 rounded" />
                  <Input value={f.accent_color} onChange={set("accent_color")} className="text-mono" />
                </div>
              </div>
            </div>
            <div className="rounded-xl border p-4 flex items-center gap-2">
              <Palette className="h-4 w-4" style={{ color: f.primary_color }} />
              <span className="text-sm">Portal buttons, gradients and charts re-theme for this institute's users.</span>
            </div>
            <Button disabled={busy} onClick={() => save({ logo_url: f.logo_url, primary_color: f.primary_color, accent_color: f.accent_color })}
              className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">
              {busy ? "Saving…" : "Save branding"}
            </Button>
          </TabsContent>

          <TabsContent value="users" className="mt-4">
            <div className="space-y-2 max-h-[440px] overflow-y-auto">
              {(users.data || []).map((u) => (
                <div key={u.id} className="flex items-center gap-2.5 rounded-xl border p-2.5">
                  <Avatar className="h-8 w-8"><AvatarFallback style={{ background: colorFor(u.id), color: "white" }} className="text-[10px] font-bold">{initialsOf(u.name)}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{u.email}</div>
                  </div>
                  <Badge variant="outline" className="capitalize">{u.role}</Badge>
                </div>
              ))}
              {users.data?.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">No users yet.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function InstitutesPage() {
  const ov = useOverview();
  const [openInst, setOpenInst] = useState(null);
  const list = ov.data?.institutes || [];

  return (
    <Shell role="superadmin" title="Institutes" subtitle="Every institute on the platform." actions={<NewInstituteDialog onCreated={ov.reload} />}>
      <div className="glass rounded-2xl p-5" data-testid="campus-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Code</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Institute</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Plan</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Email mode</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Passwordless</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((c) => (
              <TableRow key={c.id} className="cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.02]" onClick={() => setOpenInst(c)} data-testid={`campus-row-${c.code || c.id}`}>
                <TableCell className="text-mono text-xs">{c.code}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="capitalize">{c.plan || "—"}</TableCell>
                <TableCell className="capitalize">{c.email_mode || "—"}</TableCell>
                <TableCell>{c.passwordless ? "yes" : "no"}</TableCell>
                <TableCell>
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${c.is_active === false ? "text-zinc-500" : "text-emerald-600"}`}>
                    {c.is_active === false ? "inactive" : "active"}
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">No institutes yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <InstituteDrawer key={openInst?.id || "none"} inst={openInst} onClose={() => setOpenInst(null)} onSaved={() => { ov.reload(); }} />
    </Shell>
  );
}

/* ============================================================
 * Institute admins — cross-institute admin management
 * ============================================================ */
function AddAdminDialog({ institutes, onAdded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ institute_id: "", name: "", email: "", password: "", phone: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.institute_id) { toast.error("Choose an institute."); return; }
    setBusy(true);
    try {
      await api.saCreateInstituteUser(f.institute_id, { ...f, role: "admin" });
      toast.success("Admin added");
      setOpen(false);
      onAdded?.();
    } catch (err) { toast.error(err.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5"><Plus className="h-4 w-4" /> Add admin</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add institute admin</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Institute</Label>
            <Select value={f.institute_id} onValueChange={(v) => set("institute_id")(v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choose institute" /></SelectTrigger>
              <SelectContent>
                {institutes.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" required /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={set("email")} className="mt-1" required /></div>
          <div><Label>Password</Label><Input type="password" value={f.password} onChange={set("password")} className="mt-1" minLength={8} required /></div>
          <div><Label>Phone (optional)</Label><Input value={f.phone} onChange={set("phone")} className="mt-1" /></div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">{busy ? "Adding…" : "Add admin"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AdminsPage() {
  const ov = useOverview();
  const admins = useFetch(() => api.users({ role: "admin", limit: 500 }).then((r) => r?.users || []), []);
  const institutes = ov.data?.institutes || [];

  const act = async (u, patch, msg) => {
    try { await api.updateUser(u.id, patch); toast.success(msg); admins.reload(); }
    catch (e) { toast.error(e.message || "Failed"); }
  };

  return (
    <Shell role="superadmin" title="Institute Admins" subtitle="Every admin across every institute." actions={<AddAdminDialog institutes={institutes} onAdded={admins.reload} />}>
      <div className="glass rounded-2xl p-5">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Admin</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Institute</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(admins.data || []).map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7"><AvatarFallback style={{ background: colorFor(u.id), color: "white" }} className="text-[10px] font-bold">{initialsOf(u.name)}</AvatarFallback></Avatar>
                    <div><div className="font-medium">{u.name}</div><div className="text-[11px] text-muted-foreground">{u.email}</div></div>
                  </div>
                </TableCell>
                <TableCell className="text-mono text-xs">{u.institute?.code || "—"}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`h-2 w-2 rounded-full ${u.is_active ? "bg-emerald-500" : "bg-zinc-400"}`} /> {u.is_active ? "active" : "disabled"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => {
                      const pw = prompt(`New password for ${u.name}`); if (pw && pw.length >= 8) act(u, { new_password: pw }, "Password reset");
                      else if (pw) toast.error("Password must be 8+ characters");
                    }}><KeyRound className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => act(u, { role: "requester" }, "Demoted to requester")}>Demote</Button>
                    <Button size="sm" variant={u.is_active ? "outline" : "default"} className="h-8 gap-1"
                      onClick={() => act(u, { is_active: !u.is_active }, u.is_active ? "Disabled" : "Enabled")}>
                      {u.is_active ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {admins.data?.length === 0 && <TableRow><TableCell colSpan={4} className="py-10 text-center text-sm text-muted-foreground">No admins yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Shell>
  );
}

/* ============================================================
 * All users — global cross-institute browser
 * ============================================================ */
function AllUsersPage() {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const users = useFetch(() => api.users({ role: role === "all" ? undefined : role, q: q || undefined, status: status === "pending" ? "pending" : undefined, limit: 500 }).then((r) => r?.users || []), [q, role, status]);

  const act = async (u, patch, msg) => {
    try { await api.updateUser(u.id, patch); toast.success(msg); users.reload(); }
    catch (e) { toast.error(e.message || "Failed"); }
  };

  return (
    <Shell role="superadmin" title="All Users" subtitle="Search and manage every user on the platform.">
      <div className="glass rounded-2xl p-5 space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full md:w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="technician">Technician</SelectItem>
              <SelectItem value="requester">Requester</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-full md:w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending approval</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Role</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Institute</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users.data || []).map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7"><AvatarFallback style={{ background: colorFor(u.id), color: "white" }} className="text-[10px] font-bold">{initialsOf(u.name)}</AvatarFallback></Avatar>
                    <div><div className="font-medium">{u.name}</div><div className="text-[11px] text-muted-foreground">{u.email}</div></div>
                  </div>
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => act(u, { role: v }, "Role updated")}>
                    <SelectTrigger className="h-8 w-[130px] capitalize"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="requester">Requester</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-mono text-xs">{u.institute?.code || "—"}</TableCell>
                <TableCell>
                  {u.approval_status === "pending" ? (
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(u, { approval_status: "approved" }, "Approved")}>Approve</Button>
                      <Button size="sm" variant="outline" className="h-7" onClick={() => act(u, { approval_status: "rejected" }, "Rejected")}>Reject</Button>
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`h-2 w-2 rounded-full ${u.is_active ? "bg-emerald-500" : "bg-zinc-400"}`} /> {u.is_active ? "active" : "disabled"}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => {
                      const pw = prompt(`New password for ${u.name}`); if (pw && pw.length >= 8) act(u, { new_password: pw }, "Password reset");
                      else if (pw) toast.error("Password must be 8+ characters");
                    }}><KeyRound className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={u.is_active ? "outline" : "default"} className="h-8 gap-1"
                      onClick={() => act(u, { is_active: !u.is_active }, u.is_active ? "Disabled" : "Enabled")}>
                      {u.is_active ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {users.data?.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No users match.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Shell>
  );
}

/* ============================================================
 * All tickets — cross-institute browser (reuses Queue)
 * ============================================================ */
function AllTicketsPage() {
  const tickets = useFetch(() => api.tickets({ limit: 200 }).then((r) => r?.tickets || []), []);
  return (
    <Shell role="superadmin" title="All Tickets" subtitle="Every ticket, across every institute.">
      <Queue tickets={tickets.data || []} categories={[]} onChanged={tickets.reload} />
    </Shell>
  );
}

/* ============================================================
 * Platform email
 * ============================================================ */
function PlatformEmailPage() {
  const settings = useFetch(() => api.saPlatform().then((r) => r?.settings || {}), []);
  const [f, setF] = useState({ mail_provider: "resend", mail_from_email: "", mail_from_name: "", smtp_relay_url: "", provider_key: "", smtp_relay_token: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const s = settings.data;
  useEffect(() => {
    if (s && !hydrated) {
      setF((cur) => ({ ...cur, ...s, provider_key: "", smtp_relay_token: "" }));
      setHydrated(true);
    }
  }, [s, hydrated]);

  const save = async () => {
    setBusy(true);
    try {
      await api.saUpdatePlatform({
        mail_provider: f.mail_provider, mail_from_email: f.mail_from_email, mail_from_name: f.mail_from_name,
        smtp_relay_url: f.smtp_relay_url,
        ...(f.provider_key ? { provider_key: f.provider_key } : {}),
        ...(f.smtp_relay_token ? { smtp_relay_token: f.smtp_relay_token } : {}),
      });
      toast.success("Platform email settings saved");
      settings.reload();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    if (!testTo) return;
    try {
      await api.saUpdatePlatform({ test_to: testTo });
      toast.success("Test email sent");
    } catch (e) { toast.error(e.message || "Send failed"); }
  };

  return (
    <Shell role="superadmin" title="Platform Email" subtitle="Shared-domain sending defaults for institutes on 'shared' mode.">
      <div className="glass rounded-2xl p-5 max-w-xl space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="h-5 w-5 text-cobalt-500" />
          <span className="text-sm text-muted-foreground">
            Key set: {s?.provider_key_set ? "yes" : "no"} · Relay token set: {s?.relay_token_set ? "yes" : "no"}
          </span>
        </div>
        <div>
          <Label>Provider</Label>
          <Select value={f.mail_provider} onValueChange={(v) => setF((c) => ({ ...c, mail_provider: v }))}>
            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="resend">Resend</SelectItem>
              <SelectItem value="brevo">Brevo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>From email</Label><Input value={f.mail_from_email} onChange={(e) => setF((c) => ({ ...c, mail_from_email: e.target.value }))} className="mt-1" /></div>
          <div><Label>From name</Label><Input value={f.mail_from_name} onChange={(e) => setF((c) => ({ ...c, mail_from_name: e.target.value }))} className="mt-1" /></div>
        </div>
        <div><Label>Provider API key (leave blank to keep current)</Label><Input type="password" value={f.provider_key} onChange={(e) => setF((c) => ({ ...c, provider_key: e.target.value }))} className="mt-1" /></div>
        <div><Label>SMTP relay URL (default)</Label><Input value={f.smtp_relay_url} onChange={(e) => setF((c) => ({ ...c, smtp_relay_url: e.target.value }))} className="mt-1" /></div>
        <div><Label>SMTP relay token (leave blank to keep current)</Label><Input type="password" value={f.smtp_relay_token} onChange={(e) => setF((c) => ({ ...c, smtp_relay_token: e.target.value }))} className="mt-1" /></div>
        <Button disabled={busy} onClick={save} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">{busy ? "Saving…" : "Save platform email"}</Button>
        <div className="pt-3 border-t flex gap-2">
          <Input placeholder="Send test to…" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="flex-1" />
          <Button type="button" variant="outline" onClick={test} className="gap-1.5"><Send className="h-3.5 w-3.5" /> Test</Button>
        </div>
      </div>
    </Shell>
  );
}

export default function Superadmin() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="institutes" element={<InstitutesPage />} />
      <Route path="admins" element={<AdminsPage />} />
      <Route path="authorities" element={<AuthoritiesPage />} />
      <Route path="users" element={<AllUsersPage />} />
      <Route path="tickets" element={<AllTicketsPage />} />
      <Route path="email" element={<PlatformEmailPage />} />
      {/* unknown sub-path -> go home instead of rendering nothing */}
      <Route path="*" element={<Navigate to="/superadmin" replace />} />
    </Routes>
  );
}
